/*
  The fetcher throws the response body, which carries no status. It's tagged with a non-enumerable
  symbol so callers (query defaults, error UIs) can branch on the status without the tag leaking into
  serialization or clashing with a `status` field in the body.
  A body that can't carry the tag (empty, a string such as a proxy's HTML 403, frozen) is wrapped in an
  `Error` whose `data` is the raw body, so every thrown error still reports its status.
*/
const HTTP_STATUS = Symbol('httpStatus')

export type HttpErrorWithBody = Error & { data: unknown }

export const tagHttpStatus = <T>(body: T, status: number): T | HttpErrorWithBody => {
  const isTaggable = typeof body === 'object' && body !== null && !Object.isFrozen(body)
  const target = isTaggable
    ? body
    : Object.assign(new Error(typeof body === 'string' && body ? body : `HTTP ${status}`), { data: body })

  Object.defineProperty(target, HTTP_STATUS, { value: status })

  return target
}

export const getHttpStatus = (error: unknown): number | undefined =>
  typeof error === 'object' && error !== null ? (error as { [HTTP_STATUS]?: number })[HTTP_STATUS] : undefined

export const isForbiddenError = (error: unknown) => getHttpStatus(error) === 403
